//Built-in libraries
import React from "react";

//Third party libraries
import { DotsVerticalIcon, EyeOffIcon } from "@heroicons/react/outline";

//Custom components
import { DropDownMenuItem } from "../../UI/DropDown";
import SimpleDropDown, { DropDownItem } from "../../UI/SimpleDropDown";

//Services
import {
  useDashboardWidgetListUpdate,
  useDashboardWidgetsList,
} from "../../../hooks/ReactQueryHooks/useDashboardQuery";
import { HasPermission } from "../../Permission/HasPermission";

//Assets and miscellaneous
import { DashboardType } from "../../../types/Analytics";
import { WidgetListInterface } from "../types";

interface WidgetLayoutProps {
  widget: string;
  dashboard: DashboardType;
  children: React.ReactNode;
  actionsBtnLocation?: string;
}

const WidgetLayout = ({
  children,
  widget,
  dashboard,
  actionsBtnLocation = "top-4 right-2",
}: WidgetLayoutProps) => {
  const { fetchDashboardWidgets } = useDashboardWidgetsList({
    dashboard,
  });
  const dashboardWidgets = fetchDashboardWidgets?.data;
  const widgetList = dashboardWidgets?.[0]?.widgets;
  const dashboardType = dashboardWidgets?.[0]?.slug;
  const updateDashboardList = useDashboardWidgetListUpdate({
    dashboard,
  });
  const postUpdatedDashboard = (name: string) => {
    const widgets = widgetList.map((wdg: WidgetListInterface) => {
      if (wdg.slug === name) {
        return { ...wdg, isUsed: !wdg.isUsed };
      }
      return wdg;
    });
    const usedWidgetsList = widgets
      .filter((wdg: WidgetListInterface) => wdg.isUsed === true)
      .map((wdgt: WidgetListInterface) => ({ widgetId: wdgt.id }));

    const finalData = {
      widgets: usedWidgetsList,
      dashboardType: dashboardType,
    };
    updateDashboardList.mutate(finalData);
  };

  const dropdownItems: DropDownMenuItem[] = [
    {
      name: "Remove",
      clicked: () => postUpdatedDashboard(widget),
      icon: <EyeOffIcon className="h-5 w-5" />,
      permission: { of: "MANAGE_WIDGETS" },
    },
  ];

  return (
    <>
      <div className="relative h-full w-full">
        <HasPermission of="MANAGE_WIDGETS">
          <div
            className={`z-10 absolute rounded-full p-1 text-neutralColor-normal cursor-pointer hover:bg-neutralColor-light
        transition duration-300 ease-in-out ${actionsBtnLocation}`}
          >
            <SimpleDropDown toggle={<DotsVerticalIcon className="w-5 h-5" />}>
              {dropdownItems.map((item) => (
                <DropDownItem
                  id={item.name}
                  key={item.name}
                  onClick={item.clicked}
                >
                  <span>{item.icon}</span>
                  <span>{item.name}</span>
                </DropDownItem>
              ))}
            </SimpleDropDown>
          </div>
        </HasPermission>
        {children}
      </div>
    </>
  );
};

export default WidgetLayout;
